import { Link } from "react-router-dom";
import {
  KeyRound,
  ShieldCheck,
  Webhook,
  AlertTriangle,
  Eye,
  ArrowUpRight,
  Copy,
} from "lucide-react";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * StripeKeysGuide.jsx
 * ------------------------------------------------------------------
 * Step-by-step walkthrough for a merchant to find their Stripe
 * Publishable Key, Secret Key, and Webhook Signing Secret, so they
 * can paste them into their Checkout DM payment settings.
 *
 * Text-only guide — no screenshots. The Webhook Signing Secret
 * section links out to Stripe's own webhooks documentation
 * (nofollow) for anything beyond what Checkout DM listens for.
 * ------------------------------------------------------------------
 */

const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]" },
};

function StepCard({ n, accent, title, desc, note }) {
  const a = ACCENTS[accent];
  return (
    <div className="relative rounded-2xl border border-ink/10 bg-white p-5">
      <div className="flex items-start gap-3.5">
        <span className={`grid h-10 w-10 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText} font-display text-sm font-black`}>
          {n}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-ink">{title}</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">{desc}</p>
          {note && <p className="mt-2 text-[12px] leading-relaxed text-ink/45">{note}</p>}
        </div>
      </div>
    </div>
  );
}

function GuideSection({ id, icon: Icon, accent, name, tagline, what, children }) {
  const a = ACCENTS[accent];
  return (
    <section id={id} className="scroll-mt-24 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <div className="flex items-start gap-3.5">
        <span className={`grid h-12 w-12 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText}`}>
          <Icon size={20} strokeWidth={2.2} />
        </span>
        <div>
          <p className={`text-xs font-bold uppercase tracking-[0.2em] ${accent === "hotpink" ? "text-hotpink" : "text-ink/50"}`}>
            {tagline}
          </p>
          <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">{name}</h2>
        </div>
      </div>
      {what && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">{what}</p>}
      <div className="mt-6 space-y-3">{children}</div>
    </section>
  );
}

export default function StripeKeysGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Where to find your Stripe API keys and webhook signing secret",
    description: "A step-by-step guide to locating your Stripe Publishable Key, Secret Key, and Webhook Signing Secret to connect to Checkout DM.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/stripe-keys-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Stripe Keys Guide", item: "https://checkoutdm.com/stripe-keys-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Find your Stripe API Keys & Webhook Secret | Checkout DM"
        description="Step-by-step guide to locating your Stripe Secret Key and Webhook Signing Secret so you can connect Stripe to Checkout DM."
        path="/stripe-keys-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help · Stripe</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          Finding your Stripe keys
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          You'll need two things from your Stripe Dashboard to connect Stripe to Checkout
          DM: your <strong>Secret Key</strong> and a <strong>Webhook Signing Secret</strong>.
          This walks through exactly where each one lives.
        </p>

        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-4">
          <AlertTriangle size={16} className="mt-0.5 flex-none text-hotpink" />
          <p className="text-[13px] leading-relaxed text-ink/70">
            Your Secret Key and Webhook Signing Secret are sensitive — treat them like a
            password. Never paste them anywhere other than your Checkout DM Payment Settings.
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {/* API Keys */}
          <GuideSection
            id="api-keys"
            icon={KeyRound}
            accent="lilac"
            name="Secret Key"
            tagline="Step 1"
            what="This is the key Checkout DM uses to create Checkout Sessions and charge customers on your behalf."
          >
            <StepCard
              n={1}
              accent="lilac"
              title="Log in to the Stripe Dashboard"
              desc="Go to dashboard.stripe.com and sign in with your Stripe account."
            />
            <StepCard
              n={2}
              accent="lilac"
              title="Pick Test mode or Live mode"
              desc="Use the toggle in the top-right (or top-left, depending on your dashboard layout) to switch between Test mode and Live mode."
              note="Test mode and Live mode each have entirely separate keys and webhooks — make sure you're grabbing keys from the mode you actually want to connect."
            />
            <StepCard
              n={3}
              accent="lilac"
              title="Open Developers → API keys"
              desc="From the left-hand menu (or the search bar), go to Developers, then API keys."
            />
            <StepCard
              n={4}
              accent="lilac"
              title="Reveal your Secret key"
              desc="Your Publishable key is shown in full right away. Next to the Secret key, click Reveal test key (or Reveal live key) to see the full value, then copy it."
              note="Stripe lets you view the Secret key again any time you need it — unlike some providers, it isn't a one-time reveal. You can also roll (regenerate) it from this same page if it's ever exposed."
            />

            <div className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-5">
              <p className="text-sm font-bold text-ink">More on API keys</p>
              <p className="mt-2 text-[13px] leading-relaxed text-ink/70">
                Stripe documents key types, rotation, and restricted keys in more depth in their
                own guide — worth a look if you want tighter control over what a key can do.
              </p>
              <a
                href="https://docs.stripe.com/keys"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-hotpink hover:underline"
              >
                Stripe's API keys documentation
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </a>
            </div>
          </GuideSection>

          {/* Webhook */}
          <GuideSection
            id="webhook"
            icon={Webhook}
            accent="mint"
            name="Webhook Signing Secret"
            tagline="Step 2"
            what="The webhook is how Stripe tells Checkout DM the moment a Checkout Session completes. The Signing Secret lets Checkout DM verify that a webhook really came from Stripe."
          >
            <StepCard
              n={1}
              accent="mint"
              title="Open Developers → Webhooks"
              desc="Still inside Developers, click Webhooks in the left-hand menu, then click Add endpoint."
            />

            <div className="rounded-2xl border border-mint/40 bg-mint/10 p-5">
              <p className="text-sm font-bold text-ink">Your Checkout DM webhook URL</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">
                Paste this exact URL into the Endpoint URL field in Stripe — it's the same for
                every merchant, so you don't need to look it up anywhere else in your dashboard.
              </p>
              <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-ink/10 bg-white px-4 py-3">
                <code className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                  https://n8ncdm.checkoutdm.com/webhook/checkoutdm-stripe-webhook
                </code>
                <Copy size={15} className="flex-none text-ink/40" />
              </div>
            </div>

            <StepCard
              n={2}
              accent="mint"
              title="Paste in the webhook URL"
              desc="Paste the URL above into the Endpoint URL field."
            />
            <StepCard
              n={3}
              accent="mint"
              title="Choose which events to send"
              desc="Below the Endpoint URL, Stripe asks you to select the events this endpoint should listen for. This is where you tell Stripe which events are worth notifying Checkout DM about."
              note="At minimum, Checkout DM needs checkout.session.completed — that's the event that actually marks an order as paid. It's also worth adding payment_intent.succeeded and payment_intent.payment_failed if you want more granular payment-status updates, though they aren't required."
            />
            <StepCard
              n={4}
              accent="mint"
              title="Reveal the Signing secret"
              desc="Once the endpoint is created, open it from the Webhooks list. Under Signing secret, click Reveal, then copy the value (it starts with whsec_)."
              note="This is per-endpoint — if you ever delete and recreate the webhook, you'll get a new Signing secret and need to update it in Checkout DM too."
            />

            <div className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-5">
              <p className="text-sm font-bold text-ink">What each event actually means</p>
              <ul className="mt-3 space-y-2 text-[13px] leading-relaxed text-ink/70">
                <li>
                  <span className="font-semibold text-ink">checkout.session.completed</span> —
                  fires the moment a customer finishes checkout. This is the main event Checkout
                  DM uses to mark an order as paid.
                </li>
                <li>
                  <span className="font-semibold text-ink">payment_intent.succeeded</span> — the
                  underlying payment behind the session was captured successfully. Useful as a
                  secondary confirmation alongside checkout.session.completed.
                </li>
                <li>
                  <span className="font-semibold text-ink">payment_intent.payment_failed</span> —
                  the payment attempt failed. Checkout DM can use this to let the customer know
                  and, where possible, prompt them to retry.
                </li>
              </ul>
              <p className="mt-4 text-[13px] leading-relaxed text-ink/70">
                Stripe maintains the full, always-up-to-date list of webhook events — including
                ones for refunds, disputes, and subscriptions — in their own documentation. Worth
                a look if you want to go beyond what Checkout DM currently listens for.
              </p>
              <a
                href="https://docs.stripe.com/webhooks"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-hotpink hover:underline"
              >
                Stripe's Webhooks documentation
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </a>
            </div>
          </GuideSection>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
              <ShieldCheck size={17} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Where these go</p>
              <p className="mt-1 max-w-md text-sm text-ink/70">
                Paste your Secret Key and Webhook Signing Secret into the Stripe section of your
                Payment Settings in Checkout DM. Nothing is charged until this is saved and verified.
              </p>
            </div>
          </div>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
